import { React, useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { Box, Button, TextField, Typography } from "@mui/material";
import SubscriptionList from "./SubscriptionList";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../utils/colors";
import { api } from "../../utils/api";
import EmptyState from "../EmptyStates/EmptyState";
import ContractAcceptanceModal from "../../components/ContractAcceptanceModal";
import {
  subscriptionRequiresContract,
  isLinkedAddOnSubscription,
} from "../../utils/subscriptionContractGate";
import { isHiddenFromCustomerProduct } from "../../utils/hiddenFromCustomerProduct";
import usePriceChanges from "../../hooks/usePriceChanges";

const Subscription = () => {
  const navigate = useNavigate();
  const selectedPortal = sessionStorage.getItem("selectedPortal");
  const [subscriptionItems, setSubscription] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 100;
  const priceChanges = usePriceChanges(
    "subscription",
    subscriptionItems.map((subscription) => subscription._id),
    selectedPortal === "Administrator"
  );

  // Contract acceptance modal state for customers
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [pendingContractsQueue, setPendingContractsQueue] = useState([]);

  const sortNewestFirst = (subscriptions = []) =>
    [...subscriptions].sort((a, b) => new Date(b.createdOnUTC || 0) - new Date(a.createdOnUTC || 0));

  const applyPaginationMeta = (data = {}, fallbackCount = 0) => {
    const records = data.totalRecords ?? data.pagination?.totalRecords ?? fallbackCount;
    const pages =
      data.totalPages ?? data.pagination?.totalPages ?? Math.max(1, Math.ceil((records || 0) / pageSize));
    setTotalRecords(records || 0);
    setTotalPages(Math.max(1, pages || 1));
  };

  useEffect(() => {
    if (search.length > 2) {
      searchSubscriptions(search, currentPage);
    } else {
      fetchSubscriptions(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentPage]);

  // Check for pending contracts when customer loads Subscription page
  useEffect(() => {
    if (selectedPortal === "Customer") {
      checkForPendingContracts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionItems]);

  const fetchSubscriptions = async (page = 1) => {
    try {
      setLoading(true);
      let endpoint = "v1/subscription";
      if (selectedPortal === "Customer") {
        const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
        endpoint = "v1/subscription/customer/" + currentUser._id;
      }
      if (selectedPortal === "Partner") {
        endpoint = `v1/partner/company/${sessionStorage.getItem("selectedCompany")}/subscriptions`;
      }
      const response = await api(`${endpoint}?page=${page}&limit=${pageSize}`, "GET");
      if (response.data.success) {
        const subscriptions = (response.data.data.allSubscriptions || []).filter(
          (subscription) =>
            selectedPortal !== "Customer" || !isHiddenFromCustomerProduct(subscription.product)
        );
        setSubscription(sortNewestFirst(subscriptions));
        applyPaginationMeta(response.data.data, subscriptions.length);
        setCurrentPage(response.data.data.currentPage || page);
      } else {
        setSubscription([]);
        applyPaginationMeta(response.data.data || {}, 0);
      }
    } catch (error) {
      console.error("API error:", error);
      setSubscription([]);
      applyPaginationMeta({}, 0);
    }
    setLoading(false);
  };

  const searchSubscriptions = async (keyword, page = 1) => {
    setLoading(true);
    try {
      const response = await api("v1/subscription/search", "POST", {
        keyword,
        page,
        limit: pageSize,
      });
      if (response.data.success) {
        const subscriptions = response.data.data.searchResult || [];
        setSubscription(sortNewestFirst(subscriptions));
        applyPaginationMeta(response.data.data, subscriptions.length);
        setCurrentPage(response.data.data.pagination?.currentPage || page);
      } else {
        setSubscription([]);
        applyPaginationMeta(response.data.data || {}, 0);
      }
    } catch (error) {
      console.error("API error:", error);
      setSubscription([]);
      applyPaginationMeta({}, 0);
    }
    setLoading(false);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage || loading) return;
    setCurrentPage(newPage);
  };

  // Check for pending contracts across all customer subscriptions
  const checkForPendingContracts = async () => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
      if (!currentUser) {
        return;
      }

      const response = await api(`v1/subscription/customer/${currentUser._id}`, "GET");

      if (response.data.success && response.data.data.allSubscriptions) {
        const subscriptions = response.data.data.allSubscriptions;
        const pendingContracts = [];

        if (subscriptions.length > 0) {
          for (const subscription of subscriptions) {
            if (isLinkedAddOnSubscription(subscription)) {
              continue;
            }

            if (subscriptionRequiresContract(subscription.product, subscription)) {
              const contractResponse = await api(`v1/contract/check-required/${subscription._id}`, "GET");

              if (contractResponse.data.success && contractResponse.data.data.contractAcceptanceRequired) {
                const pendingResponse = await api(`v1/contract/check-pending/${subscription._id}`, "GET");

                if (pendingResponse.data.success && pendingResponse.data.data) {
                  pendingContracts.push({
                    contractData: pendingResponse.data.data,
                    subscription: subscription,
                  });
                }
              }
            }
          }
        }

        if (pendingContracts.length > 0) {
          setPendingContractsQueue(pendingContracts);
          setContractModalOpen(true);
        } else {
          setPendingContractsQueue([]);
        }
      }
    } catch (error) {
      console.error("💥 Subscription: Error checking for pending contracts:", error);
    }
  };

  const handleContractAccepted = () => {
    setContractModalOpen(false);
    setPendingContractsQueue([]);
  };

  const handleContractModalClose = () => {
    setContractModalOpen(false);
    setTimeout(() => checkForPendingContracts(), 300);
  };

  const showPagination = selectedPortal === "Administrator" && totalRecords > pageSize;

  return (
    <div className="page subscription w-full flex flex-column items-center p-4">
      {loading && <GlobalLoadingSpinner />}
      <div className="pageTitle flex flex-col w-full text-center mb-4 lg:flex-row lg:text-left lg:justify-between">
        <h2>Subscriptions</h2>
        {selectedPortal !== "Customer" && (
          <div className="pageActions flex flex-wrap items-center gap-3">
            {selectedPortal === "Administrator" && (
              <Button
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                variant="contained"
                onClick={() => navigate("/subscription/create-membership")}
              >
                Create Membership
              </Button>
            )}
            <TextField
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ width: "300px" }}
              placeholder="Search for a subscription"
            />
          </div>
        )}
      </div>
      <div className="flex w-full">
        <div className="page-content min-h-h-screen flex flex-column justify-start w-full items-center">
          <div className="flex flex-column gap-4 w-full items-center">
            {subscriptionItems.length > 0 ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ alignSelf: "flex-start", maxWidth: "calc(100vw - 200px)", width: "100%", mx: "auto" }}
                >
                  Showing newest subscriptions first
                </Typography>
                <SubscriptionList subscriptions={subscriptionItems} priceChanges={priceChanges} />
                {showPagination && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, mb: 2 }}>
                    <Button
                      variant="outlined"
                      disabled={currentPage === 1 || loading}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2">
                      Page {currentPage} of {totalPages} ({totalRecords} total)
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={currentPage >= totalPages || loading}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              !loading && <EmptyState type="merchantAccounts" text="No Subscription match your search." />
            )}
          </div>
        </div>
      </div>

      <ContractAcceptanceModal
        isOpen={contractModalOpen && pendingContractsQueue.length > 0}
        onClose={handleContractModalClose}
        pendingContracts={pendingContractsQueue}
        onContractAccepted={handleContractAccepted}
      />
    </div>
  );
};

export default Subscription;
